import React, { useState, useMemo } from 'react'
import './trade-portal-new-post.styles.scss'
import { useNavigate } from 'react-router-dom'
import { UseCloudGameTransactions } from '@/utils/firebase.utils'
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'
import BackButton from '@/components/SHARED/back-button/back-button.component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faPlus } from '@fortawesome/free-solid-svg-icons'
import ImageContainer from '../image-container/image-container.component'

const TRADEABLE_TYPES = ['inventory', 'collectible', 'lottery']

const TradePortalNewPost = ({ 
  gameData, 
  playerData, 
  gameElements 
}) => {
  const navigate = useNavigate()
  const [selectedItem, setSelectedItem] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [requests, setRequests] = useState([])
  const [addingRequest, setAddingRequest] = useState(false)
  const [reqType, setReqType] = useState('item')
  const [reqItem, setReqItem] = useState(null)
  const [reqQty, setReqQty] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const collectionProtectedItemIds = useMemo(() => {
    const ids = new Set()
    if (!playerData?.collections || !gameElements) return ids
    Object.keys(playerData.collections).forEach((collId) => {
      gameElements[collId]?.opt_itemList?.forEach((itemId) => ids.add(itemId))
    })
    return ids
  }, [playerData?.collections, gameElements])

  const offerableItems = useMemo(() => {
    if (!playerData?.items || !gameElements) return []
    return Object.entries(playerData.items)
      .filter(([itemId, data]) => {
        const el = gameElements[itemId]
        if (!el || !TRADEABLE_TYPES.includes(el.opt_itemType)) return false
        return (data.count - (collectionProtectedItemIds.has(itemId) ? 1 : 0)) > 0
      })
      .map(([itemId, data]) => {
        const el = gameElements[itemId]
        const tradeableQty = data.count - (collectionProtectedItemIds.has(itemId) ? 1 : 0)
        return { itemId, el, tradeableQty }
      })
      .sort((a, b) => a.el.name.localeCompare(b.el.name))
  }, [playerData?.items, gameElements, collectionProtectedItemIds])

  const requestableItems = useMemo(() => {
    if (!gameElements) return []
    return Object.values(gameElements)
      .filter((el) => {
        if (!TRADEABLE_TYPES.includes(el.opt_itemType)) return false
        const playerOwns = (playerData?.items?.[el.id]?.count ?? 0) > 0
        const isVisible = el.status === 'active' && el.opt_visible && el.opt_preview
        return isVisible || playerOwns
      })
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [gameElements, playerData?.items])

  function handleAddRequest() {
    if (reqType === 'currency') {
      if (reqQty < 1) return
      setRequests(prev => [...prev, { type: 'currency', itemId: null, quantity: reqQty }])
    } else {
      if (!reqItem) return
      setRequests(prev => [...prev, { type: reqItem.opt_itemType, itemId: reqItem.id, quantity: reqQty }])
    }
    setReqItem(null)
    setReqQty(1)
    setAddingRequest(false)
  }

  function getReqLabel(req) {
    if (req.type === 'currency') return `${req.quantity} Currency`
    const el = gameElements?.[req.itemId]
    return req.quantity > 1 ? `${req.quantity}× ${el?.name ?? req.itemId}` : (el?.name ?? req.itemId)
  }

  async function handleSubmit() {
    if (!selectedItem) return
    setLoading(true)
    setError(null)
    const res = await UseCloudGameTransactions('createTradePost', {
      gameId: gameData.gameId,
      playerId: playerData.playerId,
      offering: {
        type: selectedItem.el.opt_itemType,
        itemId: selectedItem.itemId,
        quantity,
      },
      requesting: requests,
    })
    setLoading(false)
    if (res?.error) { setError(res.error); return }
    navigate(-1)
  }

  return (
    <div className='tp-page'>
      <BackButton cancel={() => navigate(-1)} />
      <h2 className='tp-page-title'>New Trade</h2>
      <hr className='no-margin' />
      {error && <div className='tp-error-banner'>{error}</div>}

      <section className='tp-section'>
        <h3 className='tp-section-title'>You're giving</h3>
        {offerableItems.length === 0 ? (
          <div className='tp-empty-section'>No tradeable items in your inventory.</div>
        ) : (
          <div className='tp-icon-picker'>
            {offerableItems.map(({ itemId, el, tradeableQty }) => (
              <button
                key={itemId}
                className={`tp-pick-item${selectedItem?.itemId === itemId ? ' selected' : ''}`}
                onClick={() => { setSelectedItem({ itemId, el, tradeableQty }); setQuantity(1) }}
              >
                <div className='tp-icon-wrap medium'>
                  {el.icon
                    ? <ImageContainer src={el.icon} alt={el.name} />
                    : <span className='tp-icon-placeholder'>?</span>
                  }
                  <span className='tp-own-qty'>{tradeableQty}</span>
                </div>
                <span className='tp-pick-label'>{el.name}</span>
              </button>
            ))}
          </div>
        )}

        {selectedItem && (
          <div className='tp-qty-row'>
            <span className='tp-qty-label'>How many?</span>
            <div className='tp-qty-stepper'>
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
              <span className='tp-qty-value'>{quantity}</span>
              <button onClick={() => setQuantity(q => Math.min(selectedItem.tradeableQty, q + 1))}>+</button>
            </div>
            <span className='tp-qty-max'>max {selectedItem.tradeableQty}</span>
          </div>
        )}
      </section>

      <section className='tp-section'>
        <h3 className='tp-section-title'>
          You want <span className='tp-optional'>(optional)</span>
        </h3>

        {requests.length > 0 && (
          <div className='tp-request-chips'>
            {requests.map((req, i) => {
              const el = req.itemId ? gameElements?.[req.itemId] : {}
              return (
                <div key={i} className='tp-req-chip'>
                  {
                    req.type === 'currency'
                    ? <ImageContainer src='/images/icons/coin.png' className='tp-req-chip-icon' />
                    : <ImageContainer src={el.icon} alt='' className='tp-req-chip-icon' />
                  }
                  <span>{getReqLabel(req)}</span>
                  <button
                    className='tp-req-remove'
                    onClick={() => setRequests(prev => prev.filter((_, j) => j !== i))}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {!addingRequest && (
          <button className='tp-add-req-btn' onClick={() => setAddingRequest(true)}>
            <FontAwesomeIcon icon={faPlus} /> Add item
          </button>
        )}

        {addingRequest && (
          <div className='tp-add-req-panel'>
            <div className='tp-type-toggle'>
              <button
                className={`tp-type-btn${reqType === 'item' ? ' active' : ''}`}
                onClick={() => { setReqType('item'); setReqItem(null) }}
              >Item</button>
              <button
                className={`tp-type-btn${reqType === 'currency' ? ' active' : ''}`}
                onClick={() => { setReqType('currency'); setReqItem(null) }}
              >Currency</button>
            </div>

            {reqType === 'item' && (
              <div className='tp-icon-picker'>
                {requestableItems.map((el) => (
                  <button
                    key={el.id}
                    className={`tp-pick-item${reqItem?.id === el.id ? ' selected' : ''}`}
                    onClick={() => setReqItem(el)}
                  >
                    <div className='tp-icon-wrap medium'>
                      {el.icon
                        ? <ImageContainer src={el.icon} alt={el.name} />
                        : <span className='tp-icon-placeholder'>?</span>
                      }
                    </div>
                    <span className='tp-pick-label'>{el.name}</span>
                  </button>
                ))}
              </div>
            )}

            <div className='tp-qty-row'>
              <span className='tp-qty-label'>Qty</span>
              {reqType === 'currency' ? (
                <input
                  className='tp-qty-input'
                  type='number'
                  min='1'
                  value={reqQty}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10)
                    setReqQty(isNaN(val) || val < 1 ? 1 : val)
                  }}
                  onFocus={(e) => e.target.select()}
                />
              ) : (
                <div className='tp-qty-stepper'>
                  <button onClick={() => setReqQty(q => Math.max(1, q - 1))}>−</button>
                  <span className='tp-qty-value'>{reqQty}</span>
                  <button onClick={() => setReqQty(q => q + 1)}>+</button>
                </div>
              )}
            </div>

            <div className='tp-panel-actions'>
              <button className='g-button small-btn' onClick={() => setAddingRequest(false)}>Cancel</button>
              <button
                className='g-button small-btn primary'
                disabled={reqType === 'item' && !reqItem}
                onClick={handleAddRequest}
              >Add</button>
            </div>
          </div>
        )}
      </section>

      <div className='tp-page-footer'>
        <button className='g-button med-btn' onClick={() => navigate(-1)}>Cancel</button>
        <button
          className='g-button med-btn primary'
          disabled={!selectedItem || loading}
          onClick={handleSubmit}
        >
          {loading ? <ThreeDotsLoader /> : 'Post Trade'}
        </button>
      </div>
    </div>
  )
}

export default TradePortalNewPost
